import type { DashboardTableQueryParams } from './dashboardTable.types'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardMetricItemData } from '../../../types/dashboardMetrics.types'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')
const BUSINESS_LAYER_PATIENT_API = getEnv('REACT_APP_BUSINESS_LAYER_PATIENT')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface HuddlePeriodComparisonMetric {
  current: number
  previous: number
  percentChange: number
}

export interface HuddleYesterdayActualsParams {
  clinicId: string
  date: string
}

export interface HuddleYesterdayActualsSummary {
  production: HuddlePeriodComparisonMetric
  collections: HuddlePeriodComparisonMetric
  patientsSeen: HuddlePeriodComparisonMetric
  newPatients: HuddlePeriodComparisonMetric
}

export interface HuddleYesterdayActualsBreakdownRow {
  category: string
  amount: number
}

export interface HuddleYesterdayActualsResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleYesterdayActualsSummary
  summaryBreakdown: HuddleYesterdayActualsBreakdownRow[]
}

export const getHuddleYesterdayActuals = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdayActualsResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdayActualsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterday-actuals`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleYesterdayActualsOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdayActualsResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdayActualsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterday-actuals/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleTopPerformer {
  providerId: string
  name: string
  role: string
  production: number
}

export interface HuddleYesterdayProviderActualsSummary {
  totalProduction: HuddlePeriodComparisonMetric
  totalCollection: HuddlePeriodComparisonMetric
  topPerformer: HuddleTopPerformer | null
  patientsSeen: HuddlePeriodComparisonMetric
}

export interface HuddleProviderBreakdownRow {
  providerId: string
  provider: string
  role: string
  production: number
  collection: number
  patientsSeen: number
}

export interface HuddleYesterdayProviderActualsResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleYesterdayProviderActualsSummary
  providerBreakdown: HuddleProviderBreakdownRow[]
}

export const getHuddleYesterdayProviderActuals = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdayProviderActualsResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdayProviderActualsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterday-provider-actuals`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleYesterdayProviderActualsOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdayProviderActualsResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdayProviderActualsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterday-provider-actuals/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export type HuddleGoalMeasureUnit = 'currency' | 'count' | 'percent'

export interface HuddleGoalAttainmentRow {
  id: string
  measure: string
  goal: number | null
  actual: number
  percentOfGoal: number | null
  variance: number
  unit: HuddleGoalMeasureUnit
}

export interface HuddleYesterdayPerformanceSummary {
  production: HuddleGoalAttainmentRow
  collections: HuddleGoalAttainmentRow
}

export interface HuddleYesterdayPerformanceResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleYesterdayPerformanceSummary
  measures: HuddleGoalAttainmentRow[]
}

export const getHuddleYesterdayPerformance = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdayPerformanceResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdayPerformanceResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterday-performance`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleYesterdayPerformanceOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdayPerformanceResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdayPerformanceResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterday-performance/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleTodayForecastSummary {
  expectedProduction: HuddlePeriodComparisonMetric
  scheduledProduction: HuddlePeriodComparisonMetric
  pendingTreatments: HuddlePeriodComparisonMetric
  arDue: HuddlePeriodComparisonMetric
  arOver90: number
}

export interface HuddleTodayForecastAppointmentRow {
  appointmentId: string
  dateTime: string
  patientId: string
  patientName: string
  provider: string
  appointmentType: string
  operatory: string
  amount: number
}

export interface HuddleTodayForecastResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleTodayForecastSummary
  appointments: HuddleTodayForecastAppointmentRow[]
}

export interface HuddleTodayForecastAppointmentsResult {
  date: string
  periodStart: string
  periodEnd: string
  rows: HuddleTodayForecastAppointmentRow[]
  count: number
}

export const getHuddleTodayForecast = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTodayForecastResult> =>
  axios
    .get<
      ApiResponse<HuddleTodayForecastResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/today-forecast`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleTodayForecastAppointments = (
  params: HuddleYesterdayActualsParams & DashboardTableQueryParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTodayForecastAppointmentsResult> =>
  axios
    .get<
      ApiResponse<HuddleTodayForecastAppointmentsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/today-forecast/appointments`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleTodayForecastOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTodayForecastResult> =>
  axios
    .get<
      ApiResponse<HuddleTodayForecastResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/today-forecast/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleTomorrowForecastSummary {
  expectedProduction: HuddlePeriodComparisonMetric
  scheduledProduction: HuddlePeriodComparisonMetric
  pendingTreatments: HuddlePeriodComparisonMetric
  arDue: HuddlePeriodComparisonMetric
  arOver90: number
}

export interface HuddleTomorrowForecastAppointmentRow {
  appointmentId: string
  dateTime: string
  patientId: string
  patientName: string
  provider: string
  appointmentType: string
  operatory: string
  amount: number
  confirmationStatus: string
}

export interface HuddleTomorrowForecastResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleTomorrowForecastSummary
  appointments: HuddleTomorrowForecastAppointmentRow[]
}

export interface HuddleTomorrowForecastAppointmentsResult {
  date: string
  periodStart: string
  periodEnd: string
  rows: HuddleTomorrowForecastAppointmentRow[]
  count: number
}

export const getHuddleTomorrowForecast = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTomorrowForecastResult> =>
  axios
    .get<
      ApiResponse<HuddleTomorrowForecastResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/tomorrow-forecast`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleTomorrowForecastAppointments = (
  params: HuddleYesterdayActualsParams & DashboardTableQueryParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTomorrowForecastAppointmentsResult> =>
  axios
    .get<
      ApiResponse<HuddleTomorrowForecastAppointmentsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/tomorrow-forecast/appointments`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleTomorrowForecastOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTomorrowForecastResult> =>
  axios
    .get<
      ApiResponse<HuddleTomorrowForecastResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/tomorrow-forecast/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleTomorrowGoalsSummary {
  production: HuddleGoalAttainmentRow
  collections: HuddleGoalAttainmentRow
  patients: HuddleGoalAttainmentRow
}

export interface HuddleTomorrowGoalsResult {
  date: string
  periodStart: string
  periodEnd: string
  goals: HuddleTomorrowGoalsSummary
}

export const getHuddleTomorrowGoals = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTomorrowGoalsResult> =>
  axios
    .get<
      ApiResponse<HuddleTomorrowGoalsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/tomorrow-goals`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleTomorrowGoalsOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTomorrowGoalsResult> =>
  axios
    .get<
      ApiResponse<HuddleTomorrowGoalsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/tomorrow-goals/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleProviderPerformanceSummary {
  sameDayTreatment: HuddlePeriodComparisonMetric
  acceptedTreatment: HuddlePeriodComparisonMetric
  hourlyProduction: HuddlePeriodComparisonMetric
  perioRate: HuddlePeriodComparisonMetric
  hygieneUtilization: HuddlePeriodComparisonMetric
  collections: HuddlePeriodComparisonMetric
}

export interface HuddleProviderPerformanceRow {
  providerId: string
  provider: string
  role: string
  sameDayTreatmentPct: number | null
  acceptedTreatmentMinutes: number
  hourlyProduction: number
  perioRate: number | null
  collections: number
}

export interface HuddleProviderPerformanceResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleProviderPerformanceSummary
  providers: HuddleProviderPerformanceRow[]
}

export const getHuddleProviderPerformance = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleProviderPerformanceResult> =>
  axios
    .get<
      ApiResponse<HuddleProviderPerformanceResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/provider-performance`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleProviderPerformanceOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleProviderPerformanceResult> =>
  axios
    .get<
      ApiResponse<HuddleProviderPerformanceResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/provider-performance/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleYesterdaysPerformanceSummary {
  production: HuddlePeriodComparisonMetric
  collections: HuddlePeriodComparisonMetric
  scheduledAppointments: HuddlePeriodComparisonMetric
  productionGoal: number | null
}

export interface HuddleYesterdaysPerformanceProviderRow {
  providerId: string
  provider: string
  role: string
  production: number
  collections: number
  scheduledAppointments: number
  productionGoal: number | null
  goalAttainmentPct: number | null
}

export interface HuddleYesterdaysPerformanceResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleYesterdaysPerformanceSummary
  providers: HuddleYesterdaysPerformanceProviderRow[]
}

export const getHuddleYesterdaysPerformance = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdaysPerformanceResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdaysPerformanceResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterdays-performance`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleYesterdaysPerformanceOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdaysPerformanceResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdaysPerformanceResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterdays-performance/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleLeaderboardSummary {
  totalProduction: HuddlePeriodComparisonMetric
  topPerformer: HuddleTopPerformer | null
  practiceAverage: HuddlePeriodComparisonMetric
  overallGoalAttainmentPct: number | null
  providersAtOrAboveGoal: number
  providersWithGoals: number
}

export interface HuddleLeaderboardProviderRow {
  providerId: string
  provider: string
  role: string
  production: number
  collections: number
  appointmentsCompleted: number
  productionGoal: number | null
  goalAttainmentPct: number | null
  goalVariance: number | null
}

export interface HuddleLeaderboardResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleLeaderboardSummary
  providers: HuddleLeaderboardProviderRow[]
}

export const getHuddleLeaderboard = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleLeaderboardResult> =>
  axios
    .get<
      ApiResponse<HuddleLeaderboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/leaderboard`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleLeaderboardOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleLeaderboardResult> =>
  axios
    .get<
      ApiResponse<HuddleLeaderboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/leaderboard/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleEventPerson {
  id: string
  name: string
}

export interface HuddleEventsResult {
  date: string
  birthdays: HuddleEventPerson[]
  anniversaries: HuddleEventPerson[]
  achievements: HuddleEventPerson[]
  greetingReminders: HuddleEventPerson[]
}

export const getHuddleEvents = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleEventsResult> =>
  axios
    .get<
      ApiResponse<HuddleEventsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/events`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleEventsOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleEventsResult> =>
  axios
    .get<
      ApiResponse<HuddleEventsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/events/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleScheduleEventRow {
  id: string
  time: string
  event: string
  patientId: string | null
  patientName: string
  impact: string
}

export interface HuddleYesterdayScheduleAdjustmentsSummary {
  noShows: number
  cancellations: number
  brokenAppointments: number
  chairUtilizationPct: number
  chairUtilizationGoalPct: number | null
  adjustments: number
}

export interface HuddleYesterdayScheduleAdjustmentsResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleYesterdayScheduleAdjustmentsSummary
  events: HuddleScheduleEventRow[]
}

export const getHuddleYesterdayScheduleAdjustments = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdayScheduleAdjustmentsResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdayScheduleAdjustmentsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterday-schedule-adjustments`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleYesterdayScheduleAdjustmentsOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleYesterdayScheduleAdjustmentsResult> =>
  axios
    .get<
      ApiResponse<HuddleYesterdayScheduleAdjustmentsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/yesterday-schedule-adjustments/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HuddleTomorrowScheduleAtAGlanceSummary {
  patientsScheduled: number
  appointments: number
  providersIn: number
  openChairMinutes: number
}

export interface HuddleTomorrowScheduleAtAGlanceRow {
  appointmentId: string
  time: string
  patientId: string
  patientName: string
  provider: string
  appointmentType: string
  operatory: string
  duration: string
  status: string
}

export interface HuddleTomorrowScheduleAtAGlanceResult {
  date: string
  periodStart: string
  periodEnd: string
  summary: HuddleTomorrowScheduleAtAGlanceSummary
  appointments: HuddleTomorrowScheduleAtAGlanceRow[]
}

export interface HuddleTomorrowScheduleAtAGlanceAppointmentsResult {
  date: string
  periodStart: string
  periodEnd: string
  rows: HuddleTomorrowScheduleAtAGlanceRow[]
  count: number
}

export const getHuddleTomorrowScheduleAtAGlance = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTomorrowScheduleAtAGlanceResult> =>
  axios
    .get<
      ApiResponse<HuddleTomorrowScheduleAtAGlanceResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/tomorrow-schedule-at-a-glance`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleTomorrowScheduleAtAGlanceAppointments = (
  params: HuddleYesterdayActualsParams & DashboardTableQueryParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTomorrowScheduleAtAGlanceAppointmentsResult> =>
  axios
    .get<
      ApiResponse<HuddleTomorrowScheduleAtAGlanceAppointmentsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/tomorrow-schedule-at-a-glance/appointments`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHuddleTomorrowScheduleAtAGlanceOverview = (
  params: HuddleYesterdayActualsParams,
  options?: { signal?: AbortSignal },
): Promise<HuddleTomorrowScheduleAtAGlanceResult> =>
  axios
    .get<
      ApiResponse<HuddleTomorrowScheduleAtAGlanceResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/huddle/tomorrow-schedule-at-a-glance/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)
/**
 * Fetches the Huddle → Patients section metrics from the patient business layer.
 * `date` (YYYY-MM-DD) pins the "today" anchor; defaults to the server's today.
 */
export const getHuddlePatientMetrics = (
  date?: string,
): Promise<DashboardMetricItemData[]> =>
  axios
    .get<ApiResponse<{ metrics: DashboardMetricItemData[] }>>(
      `${BUSINESS_LAYER_PATIENT_API}/api/v1/patient/huddle/patients`,
      {
        params: date ? { date } : undefined,
      },
    )
    .then((response) => response.data.data.metrics)

/** Section-card overview: same metric tiles via the dedicated overview route. */
export const getHuddlePatientMetricsOverview = (
  date?: string,
): Promise<DashboardMetricItemData[]> =>
  axios
    .get<ApiResponse<{ metrics: DashboardMetricItemData[] }>>(
      `${BUSINESS_LAYER_PATIENT_API}/api/v1/patient/huddle/patients/overview`,
      {
        params: date ? { date } : undefined,
      },
    )
    .then((response) => response.data.data.metrics)

export type ScheduledPatientStatus =
  | 'Scheduled'
  | 'New'
  | 'Unscheduled'
  | 'Follow-up'

export interface ScheduledPatientRow {
  id: string
  patientName: string
  status: ScheduledPatientStatus
  appointmentType: string
  time: string
  provider: string
  lastVisit: string
}

/**
 * Fetches the per-patient rows for the Huddle → Patients detail table.
 * `startDate`/`endDate` (YYYY-MM-DD) bound the appointment window; both
 * default to the server's today.
 */
export const getHuddleScheduledPatients = (
  startDate?: string,
  endDate?: string,
): Promise<ScheduledPatientRow[]> => {
  const params: Record<string, string> = {}
  if (startDate) params.start_date = startDate
  if (endDate) params.end_date = endDate

  return axios
    .get<ApiResponse<{ patients: ScheduledPatientRow[] }>>(
      `${BUSINESS_LAYER_PATIENT_API}/api/v1/patient/huddle/scheduled-patients`,
      {
        params: Object.keys(params).length > 0 ? params : undefined,
      },
    )
    .then((response) => response.data.data.patients)
}
